import type { Organization, Reference } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import {
  type OrganizationConditionEntry,
  OrganizationConditionsList,
} from '@seen/core/models/organizationConditionsList'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { isOfType } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconEdit, IconPlus } from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import type { DeleteConfirmationProps } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { useShowMedplumLink } from '#src/hooks/useShowMedplumLink.ts'
import { ConditionTemplateDialog } from '#src/pages/ConditionTemplateDialog.tsx'

export interface OrganizationConditionsPageProps {
  /*
   * Override for testing - bypasses the organization query when provided.
   * Required for Storybook because MockClient doesn't support identifier queries.
   * In production, leave undefined to fetch the Seen Health organization.
   */
  _testOrganization?: ExtendedOrganization | null
  /*
   * Override for testing - bypasses the conditions list query when provided.
   * Required for Storybook because MockClient doesn't support token search
   * parameters (e.g., code=system|code) for List resources.
   * - undefined: query runs normally (production behavior)
   * - OrganizationConditionsList instance: uses provided list
   * - null: shows "no list" state
   */
  _testConditionsList?: OrganizationConditionsList | null
  /*
   * Override for testing - forces loading state when true.
   */
  _testIsLoading?: boolean
}

interface DialogState {
  open: boolean
  template: OrganizationConditionEntry | null
}

const DIALOG_CLOSED: DialogState = { open: false, template: null }

export function OrganizationConditionsPage({
  _testOrganization,
  _testConditionsList,
  _testIsLoading,
}: OrganizationConditionsPageProps = {}): JSX.Element {
  const { data: queriedOrg, isLoading: isLoadingOrg } = useSearchOne(
    ExtendedOrganization,
    _testOrganization === undefined
      ? ExtendedOrganization.querySeenHealthOrg()
      : undefined
  )
  const seenHealthOrg = _testOrganization ?? queriedOrg

  const { data: queriedConditionsList, isLoading: isLoadingList } =
    useSearchOne(
      OrganizationConditionsList,
      seenHealthOrg && _testConditionsList === undefined
        ? OrganizationConditionsList.byOrganization(seenHealthOrg.reference)
        : undefined
    )
  const conditionsList = _testConditionsList ?? queriedConditionsList

  const isReadOnly = useIsReadOnly()
  const showMedplumLink = useShowMedplumLink()
  const createMutation = useCreateMutation<OrganizationConditionsList>()
  const patchMutate = usePatchMutation<OrganizationConditionsList>().mutate

  const [dialogState, setDialogState] = useState<DialogState>(DIALOG_CLOSED)

  const conditions = useMemo(
    () =>
      [...(conditionsList?.conditions ?? [])].sort((a, b) =>
        a.title.localeCompare(b.title)
      ),
    [conditionsList]
  )

  const removeCondition = useCallback(
    (template: OrganizationConditionEntry): void => {
      if (!conditionsList) {
        return
      }
      patchMutate(
        {
          instance: conditionsList,
          mutateFn: (list) => {
            list.removeByIndex(template.index)
          },
        },
        {
          onSuccess: () =>
            toast.success(`Removed condition: ${template.title}`),
          onError: () =>
            toast.error(`Failed to remove condition: ${template.title}`),
        }
      )
    },
    [conditionsList, patchMutate]
  )

  const removeConfirmation = useCallback(
    (template: OrganizationConditionEntry): DeleteConfirmationProps => ({
      title: `Delete "${template.title}"?`,
      description:
        'This removes it from the organization conditions list and the condition picker. Existing participant conditions are unaffected, and the entry can be recovered from the list version history.',
      onConfirm: () => removeCondition(template),
    }),
    [removeCondition]
  )

  const columns: ColumnDef<OrganizationConditionEntry>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Name',
        cell: ({ row }) => (
          <div className="font-medium">{row.original.title}</div>
        ),
      },
      {
        accessorKey: 'icd10Codes',
        header: 'ICD-10',
        cell: ({ row }) => {
          const codes = row.original.icd10Codes
            .map((coding) => coding.code)
            .filter((code) => !!code)
          return codes.length > 0 ? (
            <span className="font-mono text-muted-foreground">
              {codes.join(', ')}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )
        },
      },
      {
        accessorKey: 'snomedCodes',
        header: 'SNOMED',
        cell: ({ row }) => {
          const { snomedCodes, source } = row.original
          const codes = snomedCodes
            .map((coding) => coding.code)
            .filter((code) => !!code)
          if (codes.length === 0) {
            return <span className="text-muted-foreground">—</span>
          }
          return (
            <div className="flex flex-col gap-0.5">
              <span className="font-mono text-muted-foreground">
                {codes.join(', ')}
              </span>
              {source && (
                <span className="text-xs text-muted-foreground/60">
                  {source}
                </span>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'addedBy',
        header: 'Added By',
        cell: ({ row }) => {
          const addedBy = row.original.addedBy
          if (isOfType(addedBy, 'Practitioner')) {
            return <HoverPractitioner practitioner={addedBy} />
          }
          return <span>{referenceDisplay(addedBy) ?? 'Unknown'}</span>
        },
      },
      {
        accessorKey: 'addedOn',
        header: 'Added On',
        cell: ({ row }) =>
          row.original.addedOn ? (
            <HoverDate date={row.original.addedOn} />
          ) : null,
      },
      ...(isReadOnly
        ? []
        : [
            {
              id: 'actions',
              header: '',
              cell: ({
                row,
              }: {
                row: { original: OrganizationConditionEntry }
              }) => {
                const template = row.original
                return (
                  <MoreDropdownMenu modal={false}>
                    <DropdownMenuItem
                      onSelect={() => setDialogState({ open: true, template })}
                    >
                      <IconEdit className="size-4" />
                      Edit
                    </DropdownMenuItem>
                  </MoreDropdownMenu>
                )
              },
            } satisfies ColumnDef<OrganizationConditionEntry>,
          ]),
    ],
    [isReadOnly]
  )

  if (_testIsLoading || isLoadingOrg || (!conditionsList && isLoadingList)) {
    return <Loader />
  }

  if (!seenHealthOrg) {
    return (
      <PageLayout title="Organization Conditions">
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-muted-foreground">
            Seen Health organization not found
          </p>
        </div>
      </PageLayout>
    )
  }

  const handleCreateList = (): void => {
    const list = new OrganizationConditionsList({
      source: seenHealthOrg.reference as Reference<Organization>,
    })
    createMutation.mutate(list)
  }

  if (!conditionsList) {
    return (
      <PageLayout title="Organization Conditions">
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-muted-foreground">
            No conditions list has been created for this organization yet.
          </p>
          {!isReadOnly && (
            <Button
              onClick={handleCreateList}
              loading={createMutation.isPending}
            >
              <IconPlus className="mr-2 size-4" />
              Create Conditions List
            </Button>
          )}
        </div>
      </PageLayout>
    )
  }

  return (
    <>
      <PageLayout
        title="Organization Conditions"
        actions={
          <>
            {!isReadOnly && (
              <Button
                onClick={() => setDialogState({ open: true, template: null })}
              >
                <IconPlus className="mr-2 size-4" />
                Add Condition
              </Button>
            )}
            {showMedplumLink && (
              <MoreDropdownMenu>
                <DropdownMenuMedplumLinkItem to={conditionsList} />
              </MoreDropdownMenu>
            )}
          </>
        }
      >
        {conditions.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 py-16">
            <p className="text-muted-foreground">
              No conditions have been added to the organization list yet.
            </p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={conditions}
            deleteRowProps={isReadOnly ? undefined : removeConfirmation}
          />
        )}
      </PageLayout>
      <ConditionTemplateDialog
        open={dialogState.open}
        onOpenChange={(open) => {
          if (!open) {
            setDialogState(DIALOG_CLOSED)
          }
        }}
        conditionsList={conditionsList}
        template={dialogState.template}
        isReadOnly={isReadOnly}
      />
    </>
  )
}
